/**
 * Marks an error that happened only after Firebase reported the Storage
 * object fully committed. Callers must preserve their Firestore reservation
 * on this error because the deterministic storagePath can still recover the
 * completed file.
 */
export interface UploadRecoveryError<T = unknown> extends Error {
  /** Resume only the read-back, never the transfer. The result matches the uploader's result. */
  recoverUpload?: () => Promise<T>;
  /** Kept with later class-save retries after read-back succeeds. */
  assertRecoveryAllowed?: () => void;
  code?: string;
}

export interface UploadCommittedError<T = unknown> extends UploadRecoveryError<T> {
  uploadCommitted: true;
}

export interface UploadOutcomeUnknownError<T = unknown> extends UploadRecoveryError<T> {
  uploadOutcomeUnknown: true;
}

function wrappedUploadError(error: unknown, name: string): Error & { code?: string } {
  const source = error && typeof error === 'object'
    ? error as { message?: unknown; code?: unknown; stack?: unknown }
    : null;
  const message = typeof source?.message === 'string' && source.message
    ? source.message
    : String(error || 'The uploaded file could not be confirmed.');
  const wrapped = new Error(message) as Error & { code?: string };
  wrapped.name = name;
  if (typeof source?.code === 'string' && source.code) wrapped.code = source.code;
  if (typeof source?.stack === 'string' && source.stack) wrapped.stack = source.stack;
  return wrapped;
}

export function uploadCommittedError<T = unknown>(error: unknown, recoverUpload?: () => Promise<T>, assertRecoveryAllowed?: () => void): UploadCommittedError<T> {
  const wrapped = wrappedUploadError(error, 'UploadCommittedError') as UploadCommittedError<T>;
  wrapped.uploadCommitted = true;
  if (recoverUpload) wrapped.recoverUpload = recoverUpload;
  if (assertRecoveryAllowed) wrapped.assertRecoveryAllowed = assertRecoveryAllowed;
  return wrapped;
}

export function uploadOutcomeUnknownError<T = unknown>(error: unknown, recoverUpload?: () => Promise<T>, assertRecoveryAllowed?: () => void): UploadOutcomeUnknownError<T> {
  const wrapped = wrappedUploadError(error, 'UploadOutcomeUnknownError') as UploadOutcomeUnknownError<T>;
  wrapped.uploadOutcomeUnknown = true;
  if (recoverUpload) wrapped.recoverUpload = recoverUpload;
  if (assertRecoveryAllowed) wrapped.assertRecoveryAllowed = assertRecoveryAllowed;
  return wrapped;
}

export function isUploadCommittedError(error: unknown): error is UploadCommittedError {
  return !!error
    && typeof error === 'object'
    && (error as { uploadCommitted?: unknown }).uploadCommitted === true;
}

export function isUploadOutcomeUnknownError(error: unknown): error is UploadOutcomeUnknownError {
  return !!error
    && typeof error === 'object'
    && (error as { uploadOutcomeUnknown?: unknown }).uploadOutcomeUnknown === true;
}

export function uploadNeedsRecovery(error: unknown): boolean {
  return isUploadCommittedError(error) || isUploadOutcomeUnknownError(error);
}

export function shouldRemoveUploadReservationAfterError(error: unknown): boolean {
  return !uploadNeedsRecovery(error);
}
